import React from 'react';
import RightRail, { Content, Header } from '../../../../../libs/gs-component-right-rail';
import { WeddingWebsiteQuery } from '../../../../../generated/graphql';
import ChangeThemeRail from '../../../ChangeThemeRail';
import styles from '../../preview.scss';
import { useWebsiteLayoutContext } from '../../../../../context/WebsiteLayoutContext';
import DesignRailBottomButtonBar from './SubComponents/DesignRailBottomButtonBar';
import { ConfirmationModal } from '../../../../../libs/gs-component-form';

const onSave = async (
  formChanges: Map<string, any>,
  isLoading: boolean,
  setThemeToCurrentPreview: () => Promise<void>,
  setPreviewLayoutToCurrent: () => Promise<void>
) => {
  if (isLoading) {
    return;
  }

  return Promise.all(
    Array.from(formChanges.entries()).map(([key, value]) => {
      if (key === 'previewThemeId' && !!value) {
        return setThemeToCurrentPreview();
      }

      if (key === 'previewLayout' && !!value) {
        return setPreviewLayoutToCurrent();
      }

      return Promise.resolve();
    })
  );
};

const onCancel = (
  formChanges: Map<string, any>,
  isLoading: boolean,
  resetThemeToCurrent: () => void,
  resetPreviewLayout: () => void
) => {
  if (isLoading) {
    return;
  }

  if (formChanges.get('previewThemeId')) {
    resetThemeToCurrent();
  }

  if (formChanges.get('previewLayout')) {
    resetPreviewLayout();
  }
};

const doesFormHaveUnsavedChanges = (formChanges: Map<string, any>) => {
  return Array.from(formChanges.values()).some(value => !!value);
};

interface DesignRailProps {
  onClose: () => void;
  onBack: () => void;
  previewThemeId: number | null;
  resetThemeToCurrent: () => void;
  previewThemeCallback: (id: number) => void;
  wwsData?: WeddingWebsiteQuery;
  updatingTheme: boolean;
  showErrorToast: boolean;
  setThemeToCurrentPreview: () => Promise<void>;
  onEscapeKey?: () => void;
}

const DesignRail: React.FC<DesignRailProps> = ({
  onBack,
  onClose,
  previewThemeId,
  resetThemeToCurrent,
  previewThemeCallback,
  wwsData,
  updatingTheme,
  showErrorToast,
  setThemeToCurrentPreview,
  onEscapeKey,
}) => {
  const {
    previewLayout,
    setPreviewLayoutToCurrent,
    resetPreviewLayout,
    isUpdateLayoutLoading,
  } = useWebsiteLayoutContext();
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = React.useState(false);
  const [onExitFuncAfterUnsavedChangesModal, setOnExitFuncAfterUnsavedChangesModal] = React.useState<
    () => void | undefined
  >();
  const currentThemeId = wwsData?.weddingWebsite.themeId || null;
  const isLoading = isUpdateLayoutLoading || updatingTheme;
  const isPublished = wwsData?.weddingWebsite?.state === 'published';

  const formChanges = new Map<string, any>();
  formChanges.set('previewThemeId', previewThemeId);
  formChanges.set('previewLayout', previewLayout);

  const onClosingUnsavedChangesModal = () => {
    if (onExitFuncAfterUnsavedChangesModal) {
      onExitFuncAfterUnsavedChangesModal();
      setOnExitFuncAfterUnsavedChangesModal(undefined);
    }
  };

  const handleOnCancel = () => {
    setIsUnsavedChangesModalOpen(false);
    onCancel(formChanges, isLoading, resetThemeToCurrent, resetPreviewLayout);

    onClosingUnsavedChangesModal();
  };

  const handleOnSave = async () => {
    await onSave(formChanges, isLoading, setThemeToCurrentPreview, setPreviewLayoutToCurrent);
  };

  const handleOnSaveFromModal = async () => {
    setIsUnsavedChangesModalOpen(false);
    await handleOnSave();
    onClosingUnsavedChangesModal();
  };

  const handleOnExit = (onExit?: () => void) => {
    if (onExit) {
      if (doesFormHaveUnsavedChanges(formChanges)) {
        setIsUnsavedChangesModalOpen(true);
        setOnExitFuncAfterUnsavedChangesModal(() => onExit);
      } else {
        onExit();
      }
    }
  };

  return (
    <>
      <RightRail open={true} overlay={false} onEscapeKey={() => handleOnExit(onEscapeKey)} className={styles.rightRail}>
        <Header
          leftButtonText=" "
          leftButtonOnClick={() => handleOnExit(onBack)}
          onClose={() => handleOnExit(onClose)}
          as="h1"
        >
          <span>Design</span>
        </Header>
        <Content>
          <ChangeThemeRail
            previewTheme={previewThemeCallback}
            currentThemeId={currentThemeId}
            selectedThemeId={previewThemeId || currentThemeId}
          />
        </Content>
        {doesFormHaveUnsavedChanges(formChanges) ? (
          <DesignRailBottomButtonBar
            isPublished={isPublished}
            onSave={handleOnSave}
            onCancel={() => setIsUnsavedChangesModalOpen(true)}
            isLoading={isLoading}
            showErrorToast={showErrorToast}
          />
        ) : null}
      </RightRail>

      <ConfirmationModal
        open={isUnsavedChangesModalOpen && !isLoading}
        headerText="Save changes?"
        bodyText="If you proceed without saving, your changes will be lost."
        cancelText="Cancel"
        confirmText="Save"
        onConfirm={handleOnSaveFromModal}
        onCancel={handleOnCancel}
      />
    </>
  );
};

export default DesignRail;
